import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Handshake } from "lucide-react";

const STATUSES = ["new", "contacted", "checkout_started", "paid", "won", "lost"];
const inputCls = "w-full px-2.5 py-2 border border-slate-300 rounded-sm text-sm";

export default function AdminSponsorship() {
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);

  const load = () => {
    api.get("/admin/sponsorship/config").then(({ data }) => setCfg(data));
    api.get("/admin/sponsorship/enquiries").then(({ data }) => setItems(data.items || []));
  };
  useEffect(load, []);

  const setPkg = (i, k, v) => setCfg((c) => ({ ...c, packages: c.packages.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));

  const save = async () => {
    try {
      const packages = cfg.packages.map((p) => ({ ...p, price_cad: Number(p.price_cad) || 0,
        perks: typeof p.perks === "string" ? p.perks.split("\n").map((s) => s.trim()).filter(Boolean) : p.perks }));
      const { data } = await api.put("/admin/sponsorship/config", { notification_email: cfg.notification_email || null, packages });
      setCfg(data);
      toast.success("Sponsorship packages saved");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/sponsorship/enquiries/${id}`, { status });
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <div className="label-overline text-[#E63946] flex items-center gap-2"><Handshake className="w-4 h-4" /> Sponsorship Sales</div>
        <h1 data-testid="admin-sponsorship-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Sponsor Packages & Leads</h1>
        <p className="text-xs text-slate-500 mt-1">Edit what's shown on <a href="/sponsorship" target="_blank" rel="noreferrer" className="underline">/sponsorship</a>. Every enquiry and paid sponsorship is emailed to the address below.</p>

        {cfg && (
          <div className="mt-8 bg-white border border-slate-200 rounded-sm p-6">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Lead notification email</label>
            <input data-testid="sponsorship-notify-email" className={`${inputCls} mt-1.5 max-w-sm`} value={cfg.notification_email || ""} onChange={(e) => setCfg({ ...cfg, notification_email: e.target.value })} />
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              {cfg.packages.map((p, i) => <PackageEditor key={p.id} p={p} onChange={(k, v) => setPkg(i, k, v)} />)}
            </div>
            <button data-testid="sponsorship-save" onClick={save} className="mt-5 inline-flex items-center gap-2 bg-[#E63946] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
              <Save className="w-4 h-4" /> Save Packages
            </button>
          </div>
        )}

        <h2 className="font-display text-2xl uppercase tracking-tight mt-10">Leads ({items.length})</h2>
        <div className="mt-4 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              {["Date", "Company", "Contact", "Package", "Message", "Status"].map((h) => <th key={h} className="text-left p-3 label-overline">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((x) => (
                <tr key={x.id} data-testid={`sponsor-lead-${x.id}`} className="border-b border-slate-100 align-top">
                  <td className="p-3 text-xs text-slate-500 whitespace-nowrap">{(x.created_at || "").slice(0, 10)}</td>
                  <td className="p-3 font-semibold">{x.company}{x.website && <div className="text-xs font-normal text-slate-500">{x.website}</div>}</td>
                  <td className="p-3">{x.contact_name}<div className="text-xs text-slate-500">{x.email}{x.phone ? ` · ${x.phone}` : ""}</div></td>
                  <td className="p-3">{x.package_name || "—"}</td>
                  <td className="p-3 text-xs text-slate-600 max-w-xs">{x.message || "—"}</td>
                  <td className="p-3">
                    <select data-testid={`sponsor-lead-status-${x.id}`} value={x.status} onChange={(e) => setStatus(x.id, e.target.value)}
                      className={`border rounded-sm px-2 py-1 text-xs ${x.status === "paid" ? "border-emerald-400 bg-emerald-50" : "border-slate-300"}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                    {x.logo_token && <a data-testid={`sponsor-lead-logo-link-${x.id}`} href={`/sponsorship/logo/${x.logo_token}`} target="_blank" rel="noreferrer" className="block text-[11px] text-[#E63946] underline mt-1">{x.sponsor_id ? "Logo live · manage" : "Logo upload link"}</a>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-sm text-slate-500">No sponsor leads yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

function PackageEditor({ p, onChange }) {
  const perks = Array.isArray(p.perks) ? p.perks.join("\n") : p.perks;
  return (
    <div className="border border-slate-200 rounded-sm p-4 space-y-2" data-testid={`pkg-editor-${p.id}`}>
      <input data-testid={`pkg-name-${p.id}`} className={inputCls} value={p.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Name" />
      <input data-testid={`pkg-tagline-${p.id}`} className={inputCls} value={p.tagline || ""} onChange={(e) => onChange("tagline", e.target.value)} placeholder="Tagline" />
      <input data-testid={`pkg-price-${p.id}`} type="number" min="0" className={inputCls} value={p.price_cad} onChange={(e) => onChange("price_cad", e.target.value)} placeholder="Price (CAD)" />
      <textarea data-testid={`pkg-perks-${p.id}`} rows={5} className={inputCls} value={perks} onChange={(e) => onChange("perks", e.target.value)} placeholder="One perk per line" />
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!p.featured} onChange={(e) => onChange("featured", e.target.checked)} /> Highlight as "Most Popular"</label>
    </div>
  );
}
